import type { ComponentProps, FC, PropsWithChildren } from 'react';
import { createContext, useContext, useMemo } from 'react';

import { ZoneId } from '@js-joda/core';
import { AddContextualData } from '@opendoor/observability/slim';
import type { PartnershipReferralsFragment_SellerTransactionPartnershipReferrals_referralFlags_SellerTransactionReferralFlags as PartnershipFlags } from '__generated__/graphql';
import { OdProtosSellReceptionData_SellerInput_Channel as SellerInputChannel } from '__generated__/graphql';
import { useAuthContext } from 'platform/components/AuthContext';
import { useTrackLoader } from 'platform/components/loader/LoaderContext';
import { DevError } from 'platform/Error';
import { useParams } from 'react-router';
import type { SellerProducts } from 'sellDirect/declarations/products';
import {
  extractIsPartnershipLead,
  extractPartnershipFlags,
  isAgentExperience,
} from 'sellDirect/declarations/sellerTransaction';
import type { ISellerLeadContext } from 'sellDirect/graphql/SellerLeadContext';
import { useSellerLead } from 'sellDirect/graphql/SellerLeadContext';
import type { UseParticipantsResult } from 'sellDirect/graphql/useParticipants';
import { useParticipants } from 'sellDirect/graphql/useParticipants';
import { useSellerProducts } from 'sellDirect/graphql/useSellerProducts';
import { getLeadInfo } from 'sellDirect/helpers/getLeadInfo';

// TODO: This is a temporary fix, we should bring consistency to referral data in partnership service
//  such that all agent affiliated leads have a referral.
const AGENT_CHANNELS = [
  SellerInputChannel.AGENT_REQUEST,
  SellerInputChannel.AGENT_REQUEST_LISTED,
  SellerInputChannel.SELLER_REQUEST_LISTED,
  SellerInputChannel.OUTBOUND_OFFERS_LISTED,
];

export type IQualificationContext = Pick<
  ISellerLeadContext,
  | 'lead'
  | 'customerLeads'
  | 'sellerInputUUID'
  | 'sellerTransaction'
  | 'isBuyOnlyCustomer'
  | 'isOpendoorOfferQualified'
> & {
  /** ZoneId of the lead's address. */
  zoneId: ZoneId;
  loadingQualification: boolean;
  participantsHelpers: UseParticipantsResult | undefined;
  addressUUID: string | undefined;
  shouldSeeAgentExperience: boolean;
  /** Product qualification and data for the seller */
  sellerProducts: SellerProducts;
  /** Refetch seller products (e.g. to pick up assignment changes) */
  refetchSellerProducts?: ReturnType<typeof useSellerProducts>['refetch'];
  partnershipFlags: PartnershipFlags | null;
};

/** Non-null lead from qualification context. Use this type when a consumer requires lead to be present. */
export type SellerLead = NonNullable<IQualificationContext['lead']>;

/** @internal */
export const defaultLeadContext: IQualificationContext = {
  // Seller Lead
  lead: null,
  sellerTransaction: null,
  customerLeads: [],
  sellerInputUUID: '',
  isBuyOnlyCustomer: false,
  isOpendoorOfferQualified: false,
  // Other
  zoneId: ZoneId.systemDefault(),
  loadingQualification: false,
  participantsHelpers: undefined,
  addressUUID: undefined,
  shouldSeeAgentExperience: false,
  sellerProducts: {
    availableProducts: [],
    selectedProduct: undefined,
    listingProduct: undefined,
    cashOfferProduct: undefined,
    cashPlusProduct: undefined,
    previouslyEligibleProducts: [],
  },
  partnershipFlags: null,
};

/** @internal */
export const QualificationContext = createContext<IQualificationContext>(defaultLeadContext);

/**
 * @throws error if qualification context is missing
 */
export const useQualificationContext = () => {
  const value = useContext(QualificationContext);
  if (!value) {
    throw new DevError('QualificationContext missing');
  }
  return value;
};

/**
 * QualificationProvider is a wrapper component that provides the qualification context to its children.
 *
 * @throws error if qualification context is missing
 */
export const QualificationProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    sellerInputUUID,
    lead,
    sellerTransaction,
    customerLeads,
    isBuyOnlyCustomer,
    isOpendoorOfferQualified,
    loading: sellerLeadLoading,
  } = useSellerLead();
  const { addressUUID } = useParams();

  const timeZone = lead?.address?.timeZone;
  const zoneId = useMemo(
    () => (timeZone ? ZoneId.of(timeZone) : ZoneId.systemDefault()),
    [timeZone],
  );

  // Get participant data + methods
  const participantsHelpers = useParticipants(sellerInputUUID, sellerTransaction);

  const {
    sellerProducts: sellerProductsData,
    loading: sellerProductsLoading,
    refetch: refetchSellerProducts,
  } = useSellerProducts();

  const partnershipFlags = useMemo(() => {
    return extractPartnershipFlags(sellerTransaction);
  }, [sellerTransaction]);

  const shouldSeeAgentExperience = isAgentExperience(sellerTransaction);

  // TODO: Replace this with sellerTransaction.sellerExperience === SellerExperience.AGENT_ACQUISITION
  const isAgentChannel = lead?.channel ? AGENT_CHANNELS.includes(lead?.channel) : false;

  const isPartnershipLead = extractIsPartnershipLead(sellerTransaction);

  const { isImpersonating } = useAuthContext();

  const { email, name, customerUUID: uuid } = getLeadInfo(lead);
  const contextualData = useMemo(
    () => ({
      sellerLeadUUID: lead?.id,
      sellerInputUUID,
      leadCustomer: {
        email,
        name,
        uuid,
      },
      market: lead?.address?.market?.toLowerCase(),
      isPartnershipLead,
      hasAgentReferral: partnershipFlags?.hasAgentReferral,
      isAgentChannel,
      isBuyOnlyCustomer,
      customerLeads: customerLeads?.length ?? 0,
      shouldSeeAgentExperience,
      isImpersonating,
    }),
    [
      customerLeads?.length,
      email,
      isAgentChannel,
      isBuyOnlyCustomer,
      lead?.address?.market,
      lead?.id,
      name,
      partnershipFlags?.hasAgentReferral,
      sellerInputUUID,
      shouldSeeAgentExperience,
      uuid,
      isPartnershipLead,
      isImpersonating,
    ],
  );

  const leadContext: ComponentProps<typeof AddContextualData>['leadContext'] = useMemo(
    () => ({
      sellerInputUuid: sellerInputUUID,
      sellerLeadUuid: lead?.id,
      sellerLeadMarket: lead?.address?.market?.toLowerCase(),
    }),
    [lead?.address?.market, lead?.id, sellerInputUUID],
  );

  const loadingQualification = sellerProductsLoading || sellerLeadLoading;
  useTrackLoader('provider:qualification', loadingQualification);

  const contextValue = useMemo<IQualificationContext>(
    () => ({
      lead,
      sellerTransaction,
      zoneId,
      sellerInputUUID,
      loadingQualification,
      isOpendoorOfferQualified,
      participantsHelpers,
      addressUUID,
      customerLeads,
      isBuyOnlyCustomer,
      shouldSeeAgentExperience,
      sellerProducts: sellerProductsData,
      refetchSellerProducts,
      partnershipFlags,
    }),
    [
      lead,
      sellerTransaction,
      zoneId,
      sellerInputUUID,
      loadingQualification,
      isOpendoorOfferQualified,
      participantsHelpers,
      addressUUID,
      customerLeads,
      isBuyOnlyCustomer,
      shouldSeeAgentExperience,
      sellerProductsData,
      refetchSellerProducts,
      partnershipFlags,
    ],
  );
  return (
    <QualificationContext.Provider value={contextValue}>
      <AddContextualData data={contextualData} leadContext={leadContext}>
        {children}
      </AddContextualData>
    </QualificationContext.Provider>
  );
};
